import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProducts } from '../api/api';
import ProductCard from '../components/ProductCard';
import { Coffee, Award, Sparkles, TrendingUp } from 'lucide-react';

const CATEGORIES = [
  { name: 'JUNTO® Kahveler', icon: '☕' },
  { name: 'JUNTO® Sıcak Toz Ürünler', icon: '🍫' },
  { name: 'JUNTO® Çaylar', icon: '🍵' },
  { name: 'Espresso Makineleri', icon: '⚙️' },
];

const Home = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('JUNTO® Kahveler');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    filterProductsByCategory(activeCategory);
  }, [activeCategory, allProducts]);

  const loadProducts = async (isRetry = false) => {
    try {
      if (isRetry) {
        setRetrying(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const products = await getProducts();
      setAllProducts(products);
      
      // İlk kategoriyi göster
      filterProductsByCategory('JUNTO® Kahveler', products);
      
      setError(null);
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Ürünler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  const filterProductsByCategory = (category, products = allProducts) => {
    const filtered = products.filter(p => p.category === category);
    setDisplayedProducts(filtered.slice(0, 12));
  };

  return (
    <div className="min-h-screen bg-junto-cream">
      {/* Hero Video Section */}
      <section className="relative h-[75vh] md:h-[85vh] overflow-hidden">
        <div className="absolute inset-0">
          {/* Video Background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=1920"
          >
            <source src="https://cdn.pixabay.com/video/2021/08/05/84236-585965103_large.mp4" type="video/mp4" />
          </video>
          
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
          
          {/* Content */}
          <div className="absolute inset-0 flex items-center justify-center pt-24 md:pt-0">
            <div className="container mx-auto px-4 text-center">
              <div className="max-w-4xl mx-auto">
                <div className="flex justify-center animate-fade-in">
                  <img 
                    src="/images/junto-logo.png"
                    alt="JUNTO® Kahve" 
                    className="h-[230px] sm:h-72 md:h-[25rem] lg:h-[52rem] w-auto object-contain"
                  />
                </div>
                <p className="text-xl sm:text-2xl md:text-3xl text-white mb-0 md:mb-2 font-bold -mt-20 md:-mt-40">
                  Dünya'nın En Kaliteli Kahve Çekirdekleri
                </p>
                <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-2 md:mb-6 max-w-2xl mx-auto">
                  Özenle seçilmiş, taze kavrulmuş kahve çeşitleri ile Kahve keyfini <span className="font-bold text-xl sm:text-2xl md:text-3xl">Junto'la !</span>
                </p>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center px-4">
                  <Link
                    to="/products"
                    className="bg-junto-red hover:bg-junto-red-dark text-white font-bold py-2.5 sm:py-4 px-5 sm:px-10 rounded-full text-sm sm:text-lg shadow-2xl transform hover:scale-105 transition-all w-full sm:w-auto max-w-xs sm:max-w-none"
                  >
                    <Coffee className="inline-block w-4 h-4 sm:w-6 sm:h-6 mr-1.5 sm:mr-2" />
                    Hemen Keşfet
                  </Link>
                  <a
                    href="#products"
                    className="bg-white/20 backdrop-blur-sm text-white font-bold py-2.5 sm:py-4 px-5 sm:px-10 rounded-full text-sm sm:text-lg border-2 border-white hover:bg-white/30 transition-all w-full sm:w-auto max-w-xs sm:max-w-none"
                  >
                    Ürünleri Gör
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Banner */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              Kahvenizi Nasıl İstersiniz?
            </h2>
            <p className="text-gray-600 text-lg">
              Kategorilerimizi keşfedin ve size en uygun ürünü bulun
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.name}
                to={`/products?category=${cat.name}`}
                className="bg-white border-2 border-gray-200 hover:border-junto-red rounded-xl p-6 text-center transition-all hover:shadow-lg group"
              >
                <div className="text-5xl mb-3 group-hover:scale-110 transition-transform">
                  {cat.icon}
                </div>
                <h3 className="font-bold text-gray-900 group-hover:text-junto-red transition-colors">
                  {cat.name}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Products with Sidebar */}
      <section id="products" className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-44">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Kategoriler</h3>
                <ul className="space-y-2">
                  {CATEGORIES.map((cat) => (
                    <li key={cat.name}>
                      <button
                        onClick={() => setActiveCategory(cat.name)}
                        className={`w-full text-left px-4 py-3 rounded-lg font-semibold transition-all ${
                          activeCategory === cat.name
                            ? 'bg-junto-red text-white'
                            : 'text-gray-700 hover:bg-red-50 hover:text-junto-red'
                        }`}
                      >
                        <span className="mr-2">{cat.icon}</span>
                        {cat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Products Grid with Tabs */}
            <div className="flex-1">
              {/* Tabs */}
              <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => setActiveCategory(cat.name)}
                      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                        activeCategory === cat.name
                          ? 'bg-junto-red text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Products */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {activeCategory}
                </h2>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-junto-red mb-4"></div>
                    <p className="text-gray-600">Ürünler yükleniyor...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                      <p className="text-red-600 mb-4">{error}</p>
                      <button
                        onClick={() => loadProducts(true)}
                        disabled={retrying}
                        className="bg-junto-red hover:bg-junto-red-dark text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {retrying ? 'Yeniden Deneniyor...' : 'Tekrar Dene'}
                      </button>
                    </div>
                  </div>
                ) : displayedProducts.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Bu kategoride ürün bulunamadı</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {displayedProducts.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                )}
                
                <div className="text-center mt-8">
                  <Link
                    to={`/products?category=${activeCategory}`}
                    className="inline-block bg-junto-red hover:bg-junto-red-dark text-white font-bold py-3 px-8 rounded-lg transition-all"
                  >
                    Tüm Ürünleri Gör
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl hover:bg-red-50 transition-all">
              <div className="inline-block p-4 bg-red-100 rounded-full mb-4">
                <Award className="w-12 h-12 text-junto-red" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Premium Kalite</h3>
              <p className="text-gray-600">
                Dünya çapında seçilmiş en kaliteli kahve çekirdekleri
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl hover:bg-red-50 transition-all">
              <div className="inline-block p-4 bg-red-100 rounded-full mb-4">
                <Sparkles className="w-12 h-12 text-junto-red" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Taze Kavrum</h3>
              <p className="text-gray-600">
                Her sipariş öncesi taze kavrularak en iyi aromaları sunuyoruz
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl hover:bg-red-50 transition-all">
              <div className="inline-block p-4 bg-red-100 rounded-full mb-4">
                <TrendingUp className="w-12 h-12 text-junto-red" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Hızlı Teslimat</h3>
              <p className="text-gray-600">
                Siparişleriniz özenle paketlenir ve hızlıca kapınıza gelir
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
